import { configSingleStory } from "@pub/config";
import { Typography } from "@sphtech/dmg-design-system";
import cx from "classnames";
import { ReactNode } from "react";

import styles from "./ProductComparisonTable.module.css";

type SpecsProps = {
  key?: string;
  value?: string;
};

type Props = {
  productSpecs?: {
    name: string;
    specs?: SpecsProps[];
  }[];
  caption?: string;
  hideProductNames?: boolean;
};

const Arrow = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="65"
    viewBox="0 0 64 65"
    fill="none"
  >
    <path
      d="M19 59.334L44.9615 32.334L19 5.33398"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export default function ProductComparisonTable({
  productSpecs,
  caption,
  hideProductNames,
}: Props): ReactNode {
  const buildTableData = (): string[][] => {
    const specsMap = configSingleStory.productStorySpecifications || {};
    const rows: string[][] = [];

    // Add product names row if not hidden
    if (!hideProductNames) {
      const productNames = productSpecs?.map((product) => product.name) || [];
      rows.push(["", ...productNames]);
    }

    // Add specification rows
    Object.entries(specsMap).forEach(([specKey, specName]) => {
      const specValues =
        productSpecs?.map((product) => {
          return (
            product.specs?.find((spec) => spec.key === specKey)?.value || ""
          );
        }) || [];

      // Only include rows with at least one value
      if (specValues.some(Boolean)) {
        rows.push([specName, ...specValues]);
      }
    });

    return rows;
  };

  const isHeaderCell = (rowIndex: number, colIndex: number) => {
    const isProductNamesRow = !hideProductNames && rowIndex === 0;
    const isSpecNameColumn = colIndex === 0;
    return isProductNamesRow || isSpecNameColumn;
  };

  const tableData = buildTableData();

  const { Heading } = Typography;

  return (
    <div className={styles.productSpecsContainer}>
      {Arrow}
      <div className={styles.productSpecScrollable}>
        <div className={styles.productSpecsCaption}>{caption}</div>
        <table className={styles.productSpecsTable}>
          <tbody>
            {tableData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={cx(
                  !hideProductNames &&
                    rowIndex === 0 &&
                    styles.productSpecsTableHeader,
                )}
              >
                {row.map((cell, colIndex) => {
                  return isHeaderCell(rowIndex, colIndex) ? (
                    <th key={colIndex}>
                      <Heading.SubHeading2>{cell}</Heading.SubHeading2>
                    </th>
                  ) : (
                    <td key={colIndex}>{cell}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
